import { useAuth0 } from '@auth0/auth0-react';
import { Auth0Service } from '@src/infrastructure/auth/Auth0Service';
import type { IAuthService } from '@src/domain/interfaces/auth/IAuthService';
import { useEffect, useMemo } from 'react';

/**
 * Custom hook for authentication - Presentation layer
 * Provides a clean interface to authentication service
 * Following Dependency Inversion Principle
 * Uses singleton pattern to ensure single instance of Auth0Service
 */
export const useAuth = (): IAuthService => {
  const auth0 = useAuth0();

  // Get the singleton instance and initialize it immediately
  const authService = useMemo(() => {
    const instance = Auth0Service.getInstance();
    // Initialize synchronously to avoid loading state issues
    instance.initialize({
      loginWithRedirect: auth0.loginWithRedirect,
      logout: auth0.logout,
      isAuthenticated: auth0.isAuthenticated,
      user: auth0.user,
      isLoading: auth0.isLoading,
      getAccessTokenSilently: auth0.getAccessTokenSilently,
    });
    return instance;
  }, [
    auth0.loginWithRedirect,
    auth0.logout,
    auth0.isAuthenticated,
    auth0.user,
    auth0.isLoading,
    auth0.getAccessTokenSilently,
  ]);

  // Update the singleton instance whenever auth0 values change
  useEffect(() => {
    authService.initialize({
      loginWithRedirect: auth0.loginWithRedirect,
      logout: auth0.logout,
      isAuthenticated: auth0.isAuthenticated,
      user: auth0.user,
      isLoading: auth0.isLoading,
      getAccessTokenSilently: auth0.getAccessTokenSilently,
    });
  }, [
    auth0.loginWithRedirect,
    auth0.logout,
    auth0.isAuthenticated,
    auth0.user,
    auth0.isLoading,
    auth0.getAccessTokenSilently,
    authService,
  ]);

  return authService;
};
